import React, { Suspense, lazy, useMemo } from 'react';
import { usePromptContext } from '../src/context/PromptContext';
import PromptList from './PromptList';
import PromptEditor from './PromptEditor';
import {
  LOADER_TEXT_CLASS,
  LOADER_WRAPPER_CLASS,
  PANEL_BASE_CLASS,
  PANEL_BORDER_CLASS,
  PROMPT_EDITOR_DESKTOP_WIDTH,
  PROMPT_LIST_DESKTOP_WIDTH,
} from '../layoutConstants';
import { cn } from '../utils/cn';

const TemplateManagerModal = lazy(() => import('./TemplateManagerModal'));
const PromptGenerator = lazy(() => import('./PromptGenerator'));

interface Props {
  actions: any;
  selection: any;
  sidebar: any;
}

interface PromptListPanelProps {
  actions: any;
  selection: any;
  isMobile: boolean;
}

interface PromptEditorPanelProps {
  actions: any;
  selection: any;
  isMobile: boolean;
}

interface PanelLoaderProps {
  message: string;
}

const PanelLoader: React.FC<PanelLoaderProps> = ({ message }) => (
  <div className={cn(PANEL_BASE_CLASS, 'flex-1 min-h-[200px]')} role="status" aria-live="polite">
    <div className={LOADER_WRAPPER_CLASS}>
      <span className={LOADER_TEXT_CLASS}>{message}</span>
    </div>
  </div>
);

const PromptListPanel: React.FC<PromptListPanelProps> = React.memo(({ isMobile }) => {
  const { selectedPromptId } = usePromptContext();
  
  const panelClassName = useMemo(
    () =>
      cn(
        'shrink-0',
        PANEL_BASE_CLASS,
        PANEL_BORDER_CLASS,
        isMobile
          ? selectedPromptId
            ? 'hidden'
            : 'w-full'
          : PROMPT_LIST_DESKTOP_WIDTH
      ),
    [isMobile, selectedPromptId]
  );

  return (
    <div className={panelClassName}>
      <PromptList />
    </div>
  );
});

const PromptEditorPanel: React.FC<PromptEditorPanelProps> = React.memo(({ actions, selection, isMobile }) => {
  const { selectedPromptId, handleUpdatePrompt } = usePromptContext();

  const currentPrompt = useMemo(
    () => actions.allPrompts.find((p: any) => p.id === selectedPromptId),
    [actions.allPrompts, selectedPromptId]
  );

  const panelClassName = useMemo(
    () =>
      cn(
        'flex-1 min-w-0',
        PANEL_BASE_CLASS,
        isMobile
          ? !selectedPromptId
            ? 'hidden'
            : 'w-full'
          : PROMPT_EDITOR_DESKTOP_WIDTH
      ),
    [isMobile, selectedPromptId]
  );

  // Gespeichert wird immer auf den aktuellen Stand des Eintrags mit dieser id,
  // damit Zähler wie useCount nicht überschrieben werden.
  const allPromptsRef = React.useRef(actions.allPrompts);
  allPromptsRef.current = actions.allPrompts;
  const handleSave = React.useCallback((updates: any) => {
    const base = allPromptsRef.current.find((p: any) => p.id === updates.id);
    if (base) {
      const updateFn = actions?.handleUpdatePrompt || handleUpdatePrompt;
      updateFn({ ...base, ...updates });
    }
  }, [actions?.handleUpdatePrompt, handleUpdatePrompt]);

  return (
    <div className={panelClassName}>
      {currentPrompt ? (
        <PromptEditor
          prompt={currentPrompt}
          onSave={handleSave}
          onClose={() => selection.setSelectedPromptId(null)}
          onDelete={() => actions.handleDeletePrompt(currentPrompt.id)}
          onDuplicate={(current) => actions.handleDuplicatePrompt(currentPrompt.id, current)}
          onExport={(current) => actions.handleExportPrompt(currentPrompt.id, current)}
          onTrackUse={(id: string) => actions.handleTrackPromptUse(id)}
          isMobile={isMobile}
        />
      ) : (
        <div className="h-full flex items-center justify-center bg-raised/40 px-8" role="status">
          <div className="max-w-sm">
            <div className="flex flex-col gap-2 mb-6" aria-hidden="true">
              <span className="block h-1 w-24 rounded-full bg-kind-prompt" />
              <span className="block h-1 w-16 rounded-full bg-kind-skill" />
              <span className="block h-1 w-20 rounded-full bg-kind-workflow" />
              <span className="block h-1 w-12 rounded-full bg-kind-gem" />
            </div>
            <h3 className="font-display text-2xl font-semibold text-ink leading-tight">Eintrag aus der Liste wählen</h3>
            <p className="text-sm text-graphite mt-2 leading-relaxed">
              Prompts zum Kopieren, Skills als fertige SKILL.md, Workflows als Abfolge von Schritten, Gems als Systemprompt mit Wissensdateien. Neues legen Sie oben rechts über <span className="font-medium text-ink">Neu</span> an.
            </p>
            <div className="mt-6 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs text-graphite">
              <span><kbd className="kbd">Strg</kbd> <kbd className="kbd">K</kbd></span><span>Suchen</span>
              <span><kbd className="kbd">Strg</kbd> <kbd className="kbd">N</kbd></span><span>Neuer Eintrag der gewählten Art</span>
              <span><kbd className="kbd">↑</kbd> <kbd className="kbd">↓</kbd></span><span>In der Liste blättern</span>
              <span><kbd className="kbd">?</kbd></span><span>Alle Tastenkürzel</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const MainContent: React.FC<Props> = ({ actions, selection, sidebar }) => {
  const { selectedSpecialView } = usePromptContext();
  const isMobile = sidebar.isMobile;

  if (selectedSpecialView === 'templates') {
    return (
      <div className="flex-grow flex flex-col h-full overflow-hidden bg-sheet">
        <Suspense fallback={<PanelLoader message="Vorlagen werden geladen …" />}>
          <TemplateManagerModal 
            templates={actions.templates}
            onUseTemplate={actions.handleUseTemplate}
            onUpdateTemplate={actions.handleUpdateTemplate}
            onDeleteTemplate={actions.handleDeleteTemplate}
            onClose={() => selection.setSelectedSpecialView(null)}
          />
        </Suspense>
      </div>
    );
  }

  if (selectedSpecialView === 'generator') {
    return (
      <div className="flex-grow flex flex-col h-full overflow-hidden bg-sheet">
        <Suspense fallback={<PanelLoader message="Prompt-Generator wird geladen …" />}>
          <PromptGenerator 
            onCloseAndCreate={actions.handleCloseGeneratorAndCreatePrompt}
            settings={actions.settings}
            onUpdateSettings={actions.handleUpdateSettings}
            isMobile={isMobile}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="flex flex-grow h-full">
      <PromptListPanel actions={actions} selection={selection} isMobile={isMobile} />
      <PromptEditorPanel
        actions={actions}
        selection={selection}
        isMobile={isMobile}
      />
    </div>
  );
};

export default MainContent;
